/**
 * PrepaidAccounts (driver app) · v2.7.0 batch DM
 * ===============================================
 * Driver-side "Pre-Paid Accounts" section. Shows queued assignments from
 * `GET /api/driver/prepaid/assignments`. Driver picks a customer, enters
 * To/From, taps Start Trip → meter activates with the prepaid trip
 * context. After completion, driver enters Total Amount → trip mirrors
 * to Rides/Waybills + Driver Trips and debits the customer's balance.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Wallet, Play, Square as StopIcon, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function PrepaidAccounts({ onTripStarted, onTripCompleted }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);   // current in-progress assignment + trip context
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [totalDollars, setTotalDollars] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/driver/prepaid/assignments")
      .then((r) => {
        const rows = r.data?.assignments || [];
        setList(rows);
        // Auto-resume any in_progress assignment so a driver who closed the
        // app mid-trip can pick up where they left off.
        const inProgress = rows.find((a) => a.status === "in_progress");
        if (inProgress && !active) {
          setActive({
            assignment_id: inProgress.id,
            trip_context: {
              trip_id: `PREPAID-${inProgress.id.slice(0, 8).toUpperCase()}`,
              customer_id: inProgress.customer_id,
              customer_name: inProgress.customer_name,
              origin: inProgress.origin,
              destination: inProgress.destination,
              passengers: inProgress.passengers || 1,
            },
          });
        }
      })
      .catch(() => toast.error("Could not load prepaid queue"))
      .finally(() => setLoading(false));
  }, [active]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startTrip = async () => {
    if (!selectedId) return toast.error("Pick a customer first");
    if (!origin.trim() || !destination.trim()) return toast.error("Enter pickup and drop-off");
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/prepaid/assignments/${selectedId}/start`, {
        origin: origin.trim(), destination: destination.trim(), passengers: Number(passengers) || 1,
      });
      setActive(data);
      onTripStarted?.(data.trip_context);
      toast.success(`Trip started for ${data.trip_context.customer_name} · meter active`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start trip");
    } finally { setBusy(false); }
  };

  const completeTrip = async () => {
    if (!active) return;
    const cents = Math.round(parseFloat(totalDollars || "0") * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      toast.error("Enter the total trip amount in dollars");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/prepaid/assignments/${active.assignment_id}/complete`, {
        total_cents: cents,
      });
      toast.success(`Trip complete · $${data.total_dollars.toFixed(2)} debited · $${data.balance_after_dollars.toFixed(2)} remaining`);
      if (data.overdraw_cents > 0) {
        toast.warning(`Overdraw: $${(data.overdraw_cents / 100).toFixed(2)} — collect from rider manually`, { duration: 12000 });
      }
      onTripCompleted?.(data);
      setActive(null);
      setOrigin(""); setDestination(""); setPassengers(1); setTotalDollars(""); setSelectedId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Completion failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <Card className="bg-zinc-900 border-zinc-800 p-6 text-center text-zinc-500" data-testid="prepaid-loading"><Loader2 className="h-5 w-5 animate-spin inline mr-2"/>Loading prepaid queue…</Card>;
  }

  // ACTIVE TRIP UI — completion form
  if (active) {
    const ctx = active.trip_context;
    return (
      <Card className="bg-emerald-950/30 border-2 border-emerald-400/60 p-4 space-y-3" data-testid="prepaid-active-trip">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded bg-emerald-400 text-zinc-950 flex items-center justify-center flex-shrink-0">
            <Wallet className="h-5 w-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Active prepaid trip · meter running</div>
            <div className="text-base font-extrabold text-zinc-100">{ctx.customer_name}</div>
            <div className="text-xs text-zinc-400 mt-0.5 truncate">{ctx.origin} → {ctx.destination}</div>
          </div>
        </div>
        <div className="border-t border-emerald-700/30 pt-3">
          <Label className="text-[10px] uppercase tracking-wider text-emerald-300">Total Trip Amount ($)</Label>
          <Input
            type="number" step="0.01" inputMode="decimal"
            value={totalDollars}
            onChange={(e) => setTotalDollars(e.target.value)}
            placeholder="e.g. 17.50"
            className="bg-zinc-950 border-zinc-700 text-2xl font-extrabold meter-display mt-1 h-14"
            data-testid="prepaid-total-input"
          />
          <Button
            onClick={completeTrip}
            disabled={busy || !totalDollars}
            className="w-full mt-3 bg-emerald-400 hover:bg-emerald-300 text-zinc-950 font-extrabold uppercase tracking-widest h-12"
            data-testid="prepaid-complete-btn"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin"/> : <><StopIcon className="h-5 w-5 mr-2"/>End Trip & Debit</>}
          </Button>
        </div>
      </Card>
    );
  }

  // QUEUE UI — picker + Start Trip form
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-3" data-testid="prepaid-queue">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-widest text-amber-300 flex items-center gap-2"><Wallet className="h-4 w-4"/>Pre-Paid Accounts</h3>
          <p className="text-xs text-zinc-400 mt-1">Weekly-rate customers prepaid by dispatch. Pick a customer, enter pickup + drop-off, tap Start Trip.</p>
        </div>
        <Button variant="outline" onClick={load} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="prepaid-refresh">
          <RefreshCw className="h-4 w-4"/>
        </Button>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-6 text-zinc-500" data-testid="prepaid-empty">
          No prepaid trips queued. Dispatch will pre-assign customers to your queue from the admin console.
        </div>
      ) : (
        <>
          <div className="space-y-1.5">
            {list.map((a) => {
              const bal = ((a.customer_current_balance_cents ?? a.balance_at_assignment_cents) || 0) / 100;
              return (
                <button
                  key={a.id}
                  onClick={() => setSelectedId(a.id)}
                  className={`w-full text-left rounded border p-2.5 flex items-center gap-3 transition-colors ${selectedId === a.id ? "bg-amber-400/20 border-amber-400" : "bg-zinc-950 border-zinc-800 hover:bg-zinc-900"}`}
                  data-testid={`prepaid-customer-${a.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-zinc-100 truncate">{a.customer_name}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{a.customer_phone || "—"}</div>
                  </div>
                  <div className={`text-xs font-mono font-bold ${bal < 10 ? "text-red-300" : bal < 50 ? "text-amber-300" : "text-emerald-300"}`}>
                    ${bal.toFixed(2)} {bal < 10 && <AlertTriangle className="h-3 w-3 inline ml-0.5"/>}
                  </div>
                </button>
              );
            })}
          </div>
          {selectedId && (
            <div className="border-t border-zinc-800 pt-3 space-y-2" data-testid="prepaid-start-form">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Pickup Address</Label>
              <Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Where from?" className="bg-zinc-950 border-zinc-700" data-testid="prepaid-origin"/>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Drop-off Address</Label>
              <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Where to?" className="bg-zinc-950 border-zinc-700" data-testid="prepaid-destination"/>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Passengers</Label>
                  <Input type="number" min="1" max="8" value={passengers} onChange={(e) => setPassengers(e.target.value)} className="bg-zinc-950 border-zinc-700" data-testid="prepaid-passengers"/>
                </div>
              </div>
              <Button onClick={startTrip} disabled={busy || !origin || !destination} className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-widest h-12" data-testid="prepaid-start-btn">
                {busy ? <Loader2 className="h-5 w-5 animate-spin"/> : <><Play className="h-5 w-5 mr-2"/>Start Trip · Meter ON</>}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
